<template>
  <div class="container-fluid px-4">
    <!--h4 class="text-success text-center mb-3">API Integrated</h4-->

    <BaseTable heading="Skill Levels" :rows="skillLevels" :columns="columns" searchable :selectable="false"
      :loading="loading" :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
      @searchChanged="onSearchChanged">
      <template #col-skill_level_display="{ row }">
        <div class="d-flex align-items-center gap-2">
          <!-- Spinner Input -->
          <MaterialSpinnerInput 
            :id="`spinner_${row.personid}`"
            :modelValue="row.current_skill_level || 1"
            :min="1"
            :max="10"
            :onSpinnerClick="(id) => update_spinner(id)"
            @update:modelValue="(value) => { row.current_skill_level = value; }"
          />
          <!-- Save Icon -->
          <button v-if="!readOnly"
            class="btn btn-sm btn-link p-0 m-0" 
            type="button" 
            @click="saveskilllevel(row.personid)"
            title="Save skill level"
          >
            <i class="material-icons text-success" style="font-size: 20px;">save</i>
          </button>
        </div>
      </template>
      <template #actions="{ row }">
        <MaterialButton v-if="!readOnly" size="sm" variant="text" @click="editSkill(row)">
          <i class="material-icons text-lg text-orange me-1">edit</i>
        </MaterialButton>
      </template>
    </BaseTable>

    <!-- Delete modal -->
    <DeleteModal v-model:show="showDeleteModal" :itemName="selectedSkill?.name" @confirm="deleteSkill" />

    <!-- Edit Modal -->
    <ContentModal v-model="showModal" title="Edit Skill">
      <template #default>
        <div class="p-2 d-flex flex-column gap-3">
          <div class="">
            <MaterialInput id="edit-skill-firstname" label="First Name" v-model="editForm.firstname" disabled />
          </div>
          <div class="">
            <MaterialInput id="edit-skill-lastname" label="Last Name" v-model="editForm.lastname" disabled />
          </div>
          <div class="">
            <MaterialInput id="edit-skill-email" label="Email" v-model="editForm.email" disabled />
          </div>
          <div class="">
            <MaterialSpinnerInput id="edit-skill-level" label="Skill Level"
              v-model="editForm.current_skill_level" min="1" max="10" />
          </div>
        </div>
      </template>
      <template #footer>
        <!-- <MaterialButton color="success" @click="updateSkill">
          Save Changes
        </MaterialButton> -->
        <MaterialButton @click="updateSkill" variant="primary" class="px-4 mx-2 my-0" :disabled="isSaving">
          <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="material-icons text-lg me-1">save</i>
          {{ isSaving ? 'Saving...' : 'Save' }}
        </MaterialButton>
      </template>
    </ContentModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

import BaseTable from "@/components/Table/Table.vue";
import DeleteModal from "@/components/Modal/ConfirmationModal.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import MaterialSpinnerInput from "@/components/common/MaterialSpinnerInput.vue";
import { useToast } from 'vue-toast-notification';

const props = defineProps({
    skillId: {
        type: [Number, String],
        default: null,
    },
    locationId: {
        type: [Number, String],
        default: null,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const store = useStore();
const route = useRoute();

const loading = ref(true);
const searchTerm = ref("");
const showDeleteModal = ref(false);
const selectedSkill = ref(null);
const showModal = ref(false);
const isSaving = ref(false);
const toast = useToast();

// Use props if provided, otherwise fallback to route params
const locationId = computed(() => {
    return props.locationId !== null ? Number(props.locationId) : Number(route.params.locationId);
});

const skillId = computed(() => {
    return props.skillId !== null ? Number(props.skillId) : Number(route.params.skillid);
});

// 🧠 Vuex Data
const skillLevels = computed(() => store.getters.skillLevels);
const pagination = computed(() => store.getters.skillLevelPagination);

// Table columns
const columns = [
  { key: "firstname", label: "First Name" },
  { key: "lastname", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "skill_level_display", label: "Skill Level" }, // custom slot
];

// Fetch Skill Levels from API
const fetchSkillLevels = async (page = 1, pageSize = pagination.value.pageSize || 10) => {
  loading.value = true;
  try {
    await store.dispatch("getSkillLevels", {
      skillId: skillId.value,
      locationId: locationId.value,
      search: searchTerm.value,
      page,
      pageSize,
    });
  } finally {
    loading.value = false;
  }
};

// Lifecycle
onMounted(() => fetchSkillLevels());

// Watch for skillId changes and reload data
watch(() => skillId.value, () => {
  fetchSkillLevels();
});

// Pagination Handlers
const onChangePage = async (page) => await fetchSkillLevels(page);
const onUpdatePageSize = async (size) => await fetchSkillLevels(1, size);
const onSearchChanged = async (term) => {
  searchTerm.value = term;
  await fetchSkillLevels(1);
};

// Edit/Delete/Update logic
const editForm = ref({ id: null, name: "", description: "", skillLevel: 0 });

const deleteSkill = () => {
  if (!selectedSkill.value) return;
  showDeleteModal.value = false;
};

// const saveSkill = async (row) => {
//   try {
//     loading.value = true;
//     console.log("row", row);

//     await store.dispatch("saveSkillLevel", {
//       skillId,
//       locationId,
//       skillLevel: row.current_skill_level,
//       personId: row.personid,
//     });
//     alert(`Skill level for "${row.name}" saved successfully!`);
//   } catch (err) {
//     console.error(err);
//   } finally {
//     loading.value = false;
//   }
// };

const editSkill = (row) => {
  editForm.value = { ...row };
  showModal.value = true;
};

const updateSkill = async () => {
  const level = Number(editForm.value.current_skill_level);

  if (level < 1 || level > 10) {
    toast.error("Skill level must be between 1 and 10.", {
      position: "top-right",
    });
    return;
  }

  isSaving.value = true;
  try {
    await store.dispatch("saveSkillLevel", {
      skillId: skillId.value,
      locationId: locationId.value,
      skillLevel: level,
      personId: editForm.value.personid,
    });

    showModal.value = false;
    toast.success("Skill level updated successfully!", {
      position: "top-right",
    });

    await fetchSkillLevels(
      pagination.value.currentPage || 1,
      pagination.value.pageSize || 10
    );
  } catch (err) {
    console.error(err);
    toast.error("Failed to update skill level.", {
      position: "top-right",
    });
  } finally {
    isSaving.value = false;
  }
};

// Spinner function
const update_spinner = (id) => {
  // Focus the input when clicked (spinner UI is handled by MaterialSpinnerInput component)
  const input = document.getElementById(id);
  if (input) {
    input.focus();
  }
};

const saveskilllevel = async (personId) => {
  // Find the row to get current skill level
  const row = skillLevels.value.find(r => r.personid === personId);
  if (!row) {
    toast.error("Could not find skill data for this person.", {
      position: "top-right",
    });
    return;
  }
  
  const level = Number(row.current_skill_level || 1);
  
  if (level < 1 || level > 10) {
    toast.error("Skill level must be between 0 and 10.", {
      position: "top-right",
    });
    return;
  }

  try {
    await store.dispatch("saveSkillLevel", {
      skillId: skillId.value,
      locationId: locationId.value,
      skillLevel: level,
      personId: personId,
    });

    toast.success("Skill level saved successfully!", {
      position: "top-right",
    });

    await fetchSkillLevels(
      pagination.value.currentPage || 1,
      pagination.value.pageSize || 10
    );
  } catch (err) {
    console.error(err);
    toast.error("Failed to save skill level.", {
      position: "top-right",
    });
  }
};


</script>
